import React, { useState, useContext, } from 'react';
import { Row, Col, Typography, Space, Form, Input, Select, Divider, Flex } from 'antd';
import { RightOutlined, DownOutlined } from '@ant-design/icons';
import { Card } from '../../../../UI/Card/Card.component';
import { DeviceButtonGroup, } from '../DeviceButtonGroup.component';
import { getDeviceTitle} from '../DevicesList/DevicesList.component';
import { EditSerialNumberModal } from '../../../Modals/EditSerialNumberModal.component';
import { DeviceForm } from '../DeviceForm.component';
import { IconButton } from '../../../../UI/IconButton/IconButton.component';
import { DeviceRestrictedInput } from '../DeviceRestrictedInput.component';
import { StationContext } from '../../../../../context/station-context';
import { PermissionsContext } from '../../../../../router/DashboardRoutes';
import { EditSerialNumButton } from '../EditSerialNumButton.component';

const { Item } = Form;
const { Title } = Typography;

const DISABLE_WAN = ['SNS','SNSGVR', 'SNSDFS'];

export const SNS = ({serialNum, setSerialNumber, data, isPanelPending, POS, onSaveHandler, ...props }) => {

    const userPermissions = useContext(PermissionsContext);
    const isPCI = userPermissions.server.pci;
    const allowEdit = userPermissions.device.edit;

    // Wireless Settings not available for SNSDFS, SNSGVR, SNSBASE
    // Commander EPS & Config not available for GVR and SNSBASE where POS other than Verifone
    // POS Component is not available for SNSBASE devices with POS other than GVR/Verifone
    // SNSBASE POS Verifone Display as SNSDFS
    // SNSBASE POS GVR display as SNSGVR
    

    const snsType = data.deviceType;
    const isPOSVerifone = (POS?.name === 'Verifone');
    const isPOSGVR = (POS?.name === 'GVR');
    const isGVR = (snsType === 'SNSGVR' || (snsType === 'SNSBASE' && isPOSGVR));
    const hasPOSComponent = snsType !== 'SNSBASE' || isPOSVerifone || isPOSGVR;
    const devicesWithWifiSettings = ['SNS']
    const hasWifiSettings = devicesWithWifiSettings?.includes(snsType);
    const hasCommander = !(snsType === 'SNSGVR' || (snsType === 'SNSBASE' && POS?.name !== 'Verifone'));
    const hasANT = (snsType !== 'SNSBASE');

    const [stationState, stationDispatch] = useContext(StationContext)
    const [form] = Form.useForm();
    const [displayModal, setDisplayModal] = useState(false);
    const [expandWireless, setExpandWireless] = useState(false);

    const valuesChangeHandler = (changedValues, allValues) => {
        setExpandWireless(true);
        Object.assign(allValues, {id: data.id, status: data.status, deviceType: data.deviceType});        
        stationDispatch({
            type: 'ADD_PENDING_QUEUE', 
            payload: allValues,
        })
    }

    const gvrPassportClientsOptions = Array.from({length: 13}, (_, i) => ({
        value: i + 1,
        label: (i + 1).toString(),
    }));

    const renderGVRFields = () => {
        const gvr = (
            <>
                <Item name='passportClients' label="Passport Clients:">
                    <Select options={gvrPassportClientsOptions}/>
                </Item>
                <DeviceRestrictedInput name='staticIP' label="Gilbarco Tunnel IP:">
                    <Input disabled={true}/>
                </DeviceRestrictedInput>
                <DeviceRestrictedInput name='cmdrmac' label="Passport MAC:">
                    <Input disabled={true}/>
                </DeviceRestrictedInput>
            </>
        );
        return gvr;
    }

    const renderVerifoneFields = () => {
        const verifone = (
            <>
                <div className='form__layout--vertical-inline' >
                    <DeviceRestrictedInput name='svcid' label="Verifone Service ID:">
                        <Input/>
                    </DeviceRestrictedInput>
                    <DeviceRestrictedInput name='staticIP' label="Verifone Tunnel IP:">
                        <Input disabled={true}/>
                    </DeviceRestrictedInput>
                    <DeviceRestrictedInput name='cmdrmac' label="Commander MAC:">
                        <Input disabled={true}/>
                    </DeviceRestrictedInput>
                </div>
                <Flex gap='small' wrap>
                    <DeviceButtonGroup serialNum={serialNum} config={hasCommander} status={data.status}/> 
                    <DeviceButtonGroup serialNum={serialNum} eps={hasCommander} status={data.status}/> 
                </Flex>
            </>
        );
        return verifone;
    }

    const renderWANFields = (type) => {
        
        const wan_enabled = `wan${type}Enabled`;
        const dhcp = `wan${type}DHCP`;
        const wanIP = `wan${type}IP`;
        const wanMask = `wan${type}Mask`;
        const gatewayIP = `wan${type}GatewayIP`;
        const DNSResolver1 = `wan${type}DNSResolver1`;
        const DNSResolver2 = `wan${type}DNSResolver2`;

        /*
            Requirements:
            - If WAN and DHCP are enabled: All other fields are non-editable
            - If WAN is enabled and DHCP is disabled: All other fields are editable and required
            - If WAN is disabled: All other fields (including DHCP) are non-editable
        */

        const wan = (
            <div className='form__layout--vertical-inline'>
                <Item name={wan_enabled} label='Interface Status:'>
                    { !allowEdit ? 
                        <Select disabled={true}>
                            <Select.Option value={true}>Enabled</Select.Option>
                            <Select.Option value={false}>Disabled</Select.Option>
                        </Select>
                        :
                        <Select disabled={DISABLE_WAN.includes(data.deviceType ) && type === 1 } 
                            onChange={(value) => {
                                if(value === false){
                                    form.resetFields([dhcp, wanIP, wanMask, gatewayIP, DNSResolver1, DNSResolver2])
                                }
                            }}
                            >
                            <Select.Option value={true}>Enabled</Select.Option>
                            <Select.Option value={false}>Disabled</Select.Option>
                        </Select>
                    }
                </Item>
                <Item shouldUpdate={(prev, curr) => prev.wan_enabled !== curr.wan_enabled} style={{margin: 0}}>
                    {() => {
                        const wanValue = form.getFieldValue(wan_enabled);
                        const isDisabled = !allowEdit || (wanValue === false);

                        return (
                            <Item name={dhcp} label="DHCP:">
                                <Select 
                                    disabled={isDisabled} 
                                    onChange={(value) => {
                                        if(value === true){
                                            form.resetFields([wanIP, wanMask, gatewayIP, DNSResolver1, DNSResolver2])
                                        }
                                    }}>
                                        <Select.Option value={true}>Enabled</Select.Option>
                                        <Select.Option value={false}>Disabled</Select.Option>
                                </Select>
                            </Item>
                        )
                    }}
                </Item>
                <Item shouldUpdate={(prev, curr) => prev.wan_enabled !== curr.wan_enabled || prev.dhcp !== curr.dhcp} noStyle>
                    {() => {
                        const wanValue = form.getFieldValue(wan_enabled);
                        const dhcpValue = form.getFieldValue(dhcp);

                        const isDisabled = 
                            !allowEdit || // if editing is not allowed
                            (wanValue === false) || // If WAN is disabled, disable all other
                            (wanValue === true && dhcpValue === true); // if WAN & DHCP are both enabled, disabled all other fields

                        return (
                            <>
                                <DeviceRestrictedInput label="IP:" name={wanIP} rules={[{required: !isDisabled, message: 'IP is required'}, ]}>
                                    <Input disabled={isDisabled}/>
                                </DeviceRestrictedInput>
                                <DeviceRestrictedInput name={wanMask} label="Mask (1-32):" rules={[{required: !isDisabled, message: 'Mask is required'}, ]}>
                                    <Input disabled={isDisabled}/>
                                </DeviceRestrictedInput>
                                <DeviceRestrictedInput name={gatewayIP} label="Gateway IP:" rules={[{required: !isDisabled, message: 'Gateway IP is required'}, ]}>
                                    <Input disabled={isDisabled}/>
                                </DeviceRestrictedInput>
                                <DeviceRestrictedInput name={DNSResolver1} label="DNS Resolver 1:">
                                    <Input disabled={true}/>
                                </DeviceRestrictedInput>
                                <DeviceRestrictedInput name={DNSResolver2} label="DNS Resolver 2:">
                                    <Input disabled={true}/>
                                </DeviceRestrictedInput>
                            </>
                        )
                    }}
                </Item>
            </div>
        )

        return wan;
    }

    const getInitialValues = () => {
        const clonedObj = Object.assign({}, data)
        const selectFields = ['licensed', 'wan1Enabled', 'wan2Enabled', 'wan1DHCP', 'wan2DHCP',]
        Object.keys(clonedObj).forEach(key => {
            if(!clonedObj[key] && !selectFields.includes(key)){
                clonedObj[key] = 'N/A';
            }
        });
        
        return clonedObj;
    }
        
    const wifiSettings = (
        <> 
            <Card bordered={true} className="device__details">
                <Flex align='bottom'>
                    <IconButton 
                        type='link'
                        tooltip={isPanelPending(data) ? 'Pending changes must be cleared' : ''} 
                        disabled={isPanelPending(data)} 
                        allowHover={false} onClick={() => setExpandWireless(!expandWireless)}
                        icon={ !expandWireless ? <RightOutlined/> : <DownOutlined/>}
                        iconPosition="end"
                        style={{paddingLeft: 0}}
                        />
                    <Title level={5} style={{color: 'var(--primary-color)', fontWeight: 800, marginTop: 2}}>Wi-Fi Settings</Title>
                </Flex>
                
                <div className={`card-content ${!expandWireless ? 'expanded' : 'collapsed'}`}>
                    {/* WiFi Store -- WiFi 1 */}
                    <Divider style={{marginTop: 10 }}/>
                    <div className='form__layout--vertical-inline'>
                        <Item name='wifi1Enabled' label="Wi-Fi Store:">
                            <Select disabled={!isPCI || !allowEdit} >
                                <Select.Option value={true}>Enabled</Select.Option>
                                <Select.Option value={false}>Disabled</Select.Option>
                            </Select>
                        </Item>
                        <DeviceRestrictedInput name='wifi1SSID' label="Network Name (SSID):">
                            <Input disabled={!isPCI || !allowEdit} />
                        </DeviceRestrictedInput>
                        <DeviceRestrictedInput name='wifi1Password' label="Password:">
                            <Input.Password disabled={!isPCI || !allowEdit}/>
                        </DeviceRestrictedInput>
                        <Item name='wifi1Channel' label="Channel:">
                            <Select placeholder='Select Channel' disabled={!isPCI || !allowEdit}>
                                <Select.Option value={1}>1</Select.Option>
                                <Select.Option value={2}>2</Select.Option>
                                <Select.Option value={3}>3</Select.Option>
                                <Select.Option value={4}>4</Select.Option>
                                <Select.Option value={5}>5</Select.Option>
                            </Select>
                        </Item>
                        <span/>
                    </div>
                    
                    {/* WiFi Public -- WiFi 2 */}
                    <Divider style={{marginTop: 10 }}/>
                    <div className='form__layout--vertical-inline' >
                        <Item name='wifi2Enabled' label="Wi-Fi Public:">
                            <Select disabled={!isPCI || !allowEdit}>
                                <Select.Option value={true}>Enabled</Select.Option>
                                <Select.Option value={false}>Disabled</Select.Option>
                            </Select>
                        </Item>
                        <DeviceRestrictedInput name='wifi2SSID' label="Network Name (SSID):">
                            <Input disabled={!isPCI || !allowEdit}/>
                        </DeviceRestrictedInput>
                        <DeviceRestrictedInput name='wifi2Password' label="Password:">
                            <Input.Password disabled={!isPCI || !allowEdit} />
                        </DeviceRestrictedInput>
                        <Item name='wifi2Channel' label="Channel:">
                            <Select placeholder='Select Channel' disabled={!isPCI || !allowEdit}>
                                <Select.Option value={1}>1</Select.Option>
                                <Select.Option value={2}>2</Select.Option>
                                <Select.Option value={3}>3</Select.Option>
                                <Select.Option value={4}>4</Select.Option>
                                <Select.Option value={5}>5</Select.Option>
                            </Select>
                        </Item>
                        <span/>
                    </div>
                </div>
            </Card>
        </>
    );

    return(
        <>
            <div className={`device__${snsType}`}>
                <DeviceForm 
                    form={form}
                    colon={true}
                    name={`${snsType}-${data.id}-device-form`}
                    layout='vertical'
                    initialValues={{
                        ...data, 
                        passportClients: data?.passportClients || 1, // Override or set default value: 1
                    }}
                    // initialValues={getInitialValues()}
                    onValuesChange={(changedValues, allValues) => valuesChangeHandler(changedValues, allValues)}
                    onFinish={() => onSaveHandler([data.id])}
                    scrollToFirstError={true}
                >
                    <Flex gap='middle' vertical>
                        <Card bordered={true} className="device__details">
                            <div className='form__layout--vertical-inline' >
                                <EditSerialNumButton 
                                    serialNum={serialNum}
                                    devicePending={isPanelPending(data)}
                                    onClick={() => setDisplayModal(true)}
                                />
                                <DeviceRestrictedInput name='version' label="Firmware Version:">
                                    <Input disabled={true}/>
                                </DeviceRestrictedInput>
                                <DeviceRestrictedInput name='routerversion' label="OS Version:">
                                    <Input disabled={true}/>
                                </DeviceRestrictedInput>
                                <Item name='licensed' label="License:">
                                    <Select>
                                        <Select.Option value={1}>Licensed</Select.Option>
                                        <Select.Option value={0}>Unlicensed</Select.Option>
                                    </Select>
                                </Item>
                                <DeviceRestrictedInput name='publicIP' label="Public IP:">
                                    <Input disabled={true}/>
                                </DeviceRestrictedInput>
                                <DeviceRestrictedInput name='comment' label='Description:' >
                                    <Input.TextArea autoSize={{maxRows: 1}}/>
                                </DeviceRestrictedInput>
                            </div>
                            <Row>
                                <Space size="small" style={{overflowX: 'auto'}}>
                                    <DeviceButtonGroup serialNum={serialNum} ant={hasANT} winbox status={data.status}/> 
                                </Space>
                            </Row>
                        </Card>
                        
                        {/* POS SECTION */}
                        <Row gutter={[16,16]} wrap>
                            { hasPOSComponent &&
                                <Col xs={24} sm={24} lg={8} xxl={hasWifiSettings ? 6 : 8}>
                                    <Card bordered={true} className="device__details" >
                                        { isGVR ? 
                                            <>
                                                <Title level={5} style={{color: 'var(--primary-color)', fontWeight: 800}}>Gilbarco Veeder-Root</Title>
                                                <Divider style={{marginTop: 10 }}/>
                                                <div className='form__layout--vertical-inline' >
                                                    { renderGVRFields() }
                                                </div>
                                            </>
                                        :
                                            <>
                                                <Title level={5} style={{color: 'var(--primary-color)', fontWeight: 800}}>Verifone</Title>
                                                <Divider style={{marginTop: 10 }}/>
                                                { renderVerifoneFields() }
                                            </>
                                        }
                                    </Card>
                                </Col>
                            }

                            {/* WAN SECTION  */}
                            <Col xs={24} sm={24} lg={hasPOSComponent ? 8 : 12} xxl={hasWifiSettings ? 6 : 8}>
                                <Card bordered={true} className="device__details">
                                    <Title level={5} style={{color: 'var(--primary-color)', fontWeight: 800}}>WAN 1</Title>
                                    <Divider style={{marginTop: 10 }}/>
                                    { renderWANFields(1) }
                                </Card>
                            </Col>
                            <Col xs={24} sm={24} lg={hasPOSComponent ? 8 : 12} xxl={hasWifiSettings ? 6 : 8}>
                                <Card bordered={true} className="device__details">
                                    <Title level={5} style={{color: 'var(--primary-color)', fontWeight: 800}}>WAN 2</Title>
                                    <Divider style={{marginTop: 10 }}/>
                                    { renderWANFields(2) }
                                </Card>
                            </Col>

                            {/* WIFI SECTION */}
                            {hasWifiSettings && 
                                <Col sm={24} xxl={6}>
                                    {wifiSettings}
                                </Col>
                            }
                        </Row>
                    </Flex>
                </DeviceForm>

                { displayModal && <EditSerialNumberModal displayField={false} deviceType={getDeviceTitle(data.deviceType)} id={data.id} serialNum={serialNum} visible={displayModal} setVisible={setDisplayModal} /> }

            </div>
        </>
    );
}